import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatEuro } from "../lib/format";
import { Loader2, ChevronRight, Users } from "lucide-react";
import { toast } from "sonner";
import SegnalatoreGestioneDialog, { SEG_GEST_RESTORE } from "./SegnalatoreGestioneDialog";

const readRestore = () => {
  try {
    const r = JSON.parse(sessionStorage.getItem(SEG_GEST_RESTORE) || "null");
    if (r && r.path === window.location.pathname && Date.now() - (r.ts || 0) < 4 * 3600e3) return r;
  } catch { /* ignore */ }
  return null;
};

export const RiepilogoSegnalatoriPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [initial, setInitial] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studio/riepilogo-segnalatori");
      const list = (data || []).filter((r) => r.compenso_configurato);
      setRows(list);
      const rs = readRestore();
      if (rs) {
        sessionStorage.removeItem(SEG_GEST_RESTORE);
        const row = list.find((r) => r.segnalatore_key === rs.key);
        if (row) { setInitial(rs); setSel(row); }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totProvv = rows.reduce((s, r) => s + Number(r.provvigione || 0), 0);
  const totOnorari = rows.reduce((s, r) => s + Number(r.tot_onorari || 0), 0);

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  return (
    <div data-testid="riepilogo-segnalatori-panel">
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <div className="pgp-card p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Segnalatori</div>
          <div className="text-2xl font-head font-extrabold text-slate-50 mt-1" data-testid="riepilogo-count">{rows.length}</div>
        </div>
        <div className="pgp-card p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Totale onorari</div>
          <div className="text-2xl font-head font-extrabold text-slate-200 mt-1 font-mono">{formatEuro(totOnorari)}</div>
        </div>
        <div className="pgp-card p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Provvigioni maturate</div>
          <div className="text-2xl font-head font-extrabold text-teal-400 mt-1 font-mono" data-testid="riepilogo-tot-provvigione">{formatEuro(totProvv)}</div>
        </div>
      </div>

      <div className="pgp-card overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
            <Users className="h-6 w-6 text-slate-600" />
            Nessuna provvigione da mostrare. Assegna un segnalatore alle pratiche e configura il Compenso nella sua scheda anagrafica.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="riepilogo-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                <th className="px-5 py-3">Segnalatore</th>
                <th className="px-5 py-3">Compenso</th>
                <th className="px-5 py-3 text-right">Pratiche</th>
                <th className="px-5 py-3 text-right">Quietanze</th>
                <th className="px-5 py-3 text-right">Tot. Onorari</th>
                <th className="px-5 py-3 text-right">Provvigione</th>
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-slate-800/40 transition-colors cursor-pointer" data-testid={`riepilogo-row-${i}`} onClick={() => setSel(r)}>
                  <td className="px-5 py-3.5 font-medium text-slate-100">{r.segnalatore}</td>
                  <td className="px-5 py-3.5 text-slate-400 text-xs">{r.perc > 0 ? `${r.perc}% ${r.base || ""}` : "—"}</td>
                  <td className="px-5 py-3.5 text-right font-mono text-slate-300">{r.n_pratiche}</td>
                  <td className="px-5 py-3.5 text-right font-mono text-slate-300">{r.n_quietanze}</td>
                  <td className="px-5 py-3.5 text-right font-mono text-slate-300">{formatEuro(r.tot_onorari)}</td>
                  <td className="px-5 py-3.5 text-right font-mono text-teal-400 font-semibold">{formatEuro(r.provvigione)}</td>
                  <td className="px-5 py-3.5 text-right"><ChevronRight className="h-4 w-4 text-slate-500 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {sel && (
        <SegnalatoreGestioneDialog
          open={!!sel}
          onOpenChange={(o) => { if (!o) { setSel(null); setInitial(null); } }}
          initial={initial}
          data={rows.find((r) => r.segnalatore_key === sel.segnalatore_key) || sel}
          onChanged={load}
        />
      )}
    </div>
  );
};
